import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DatePicker component for selecting dates.
 *
 * @component
 * @example
 * ```tsx
 * <DatePicker
 *   value={selectedDate}
 *   label="Select a date"
 *   onChange={handleDateChange}
 *   disabled={false}
 *   placeholder="Select a date"
 *   size="medium"
 *   className="custom-datepicker"
 * />
 * ```
 */
import { ErrorList, InputLabelWrapper } from '@guildeducationinc/recess';
import { InputAdornment } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { CalendarIcon } from '@mui/x-date-pickers/icons';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import classNames from 'classnames';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import utc from 'dayjs/plugin/utc';
import { uniqueId } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useBreakpoints, useLocale } from '../../hooks';
import styles from './DatePicker.module.scss';
dayjs.extend(utc);
const DATE_FORMAT = 'YYYY-MM-DD';
/**
 * DatePicker component for selecting dates.
 *
 * @param props - The DatePicker component props.
 * @returns The rendered DatePicker component.
 */
export const DatePicker = (props) => {
    const { locale } = useLocale();
    const { isMobile } = useBreakpoints();
    const ref = useRef(null);
    const inputId = useMemo(() => uniqueId('date-picker'), []);
    const errorList = props.errors
        ? Array.isArray(props.errors)
            ? props.errors
            : [props.errors]
        : [];
    const handleChange = (value) => {
        props.onChange(value?.isValid() ? value.format(DATE_FORMAT) : undefined);
    };
    useEffect(() => {
        if (ref.current)
            ref.current.id = inputId;
    }, [inputId]);
    return (_jsx(LocalizationProvider, { adapterLocale: locale.split('-')[0], dateAdapter: AdapterDayjs, children: _jsxs("div", { children: [_jsx(InputLabelWrapper, { inputId: inputId, hideLabel: props.hideLabel || !props.label, label: props.label, additionalInfo: props.additionalInfo, helpText: props.helpText, invalid: errorList.length > 0 }), _jsx(MuiDatePicker, { inputRef: ref, label: '', value: props.value ? dayjs(props.value, DATE_FORMAT) : null, onChange: handleChange, disabled: props.disabled, "data-testid": `date-picker-${props.label}`, closeOnSelect: true, slotProps: {
                        actionBar: {
                            actions: ['clear'],
                        },
                        textField: {
                            size: props.size ?? 'small',
                            placeholder: props.placeholder,
                            InputProps: !isMobile
                                ? undefined
                                : {
                                    endAdornment: (_jsx(InputAdornment, { position: 'end', children: _jsx(CalendarIcon, {}) })),
                                },
                        },
                        mobilePaper: {
                            className: styles.datePickerPaper,
                        },
                        desktopPaper: {
                            className: styles.datePickerPaper,
                        },
                    }, className: classNames(props.className, styles.datePickerInput), sx: {
                        border: '1px solid #000',
                        borderRadius: '5px',
                        maxHeight: '44px',
                        maxWidth: '300px',
                        backgroundColor: 'rgb(248, 242, 233)',
                    } }), _jsx(ErrorList, { errors: errorList, show: errorList.length > 0, global: false })] }) }));
};
